/* global axios */
import ApiClient from '../ApiClient';

// VoiceInboxConfig — client for the per-inbox AI Voice config endpoint
// (Api::V2::Accounts::Voice::InboxConfigsController). The §10.1 settings
// page reads + writes the full §9.4 schema through these two methods.
class VoiceInboxConfig extends ApiClient {
  constructor() {
    super('voice/inboxes', { apiVersion: 'v2', accountScoped: true });
  }

  show(inboxId) {
    return axios.get(`${this.url}/${inboxId}/config`);
  }

  update(inboxId, config) {
    return axios.patch(`${this.url}/${inboxId}/config`, { config });
  }

  // Sprint 4 polish — playground for the AI receptionist. The
  // frontend keeps the turn history; backend is stateless and replays
  // through Voice::Receptionist::LlmService on each call.
  receptionistPreview(inboxId, { message, history = [] } = {}) {
    return axios.post(`${this.url}/${inboxId}/receptionist_preview`, {
      message,
      history,
    });
  }
}

// VoiceCalls — client for the call inspection endpoint backing the
// §10.2 conversation card.
class VoiceCalls extends ApiClient {
  constructor() {
    super('voice/calls', { apiVersion: 'v2', accountScoped: true });
  }

  show(callId) {
    return axios.get(`${this.url}/${callId}`);
  }

  toggleActionItem(callId, index, done) {
    return axios.patch(`${this.url}/${callId}/action_items/${index}`, { done });
  }

  // Sprint 4 — sentiment trend aggregation backing the dashboard chart.
  sentimentTrends({ windowDays = 30, inboxId = null } = {}) {
    return axios.get(`${this.url}/sentiment_trends`, {
      params: { window_days: windowDays, inbox_id: inboxId },
    });
  }

  reSummarize(callId) {
    return axios.post(`${this.url}/${callId}/re_summarize`);
  }

  reTranscribe(callId) {
    return axios.post(`${this.url}/${callId}/re_transcribe`);
  }

  list(params = {}) {
    return axios.get(this.url, { params });
  }

  // Conversation-scoped list (§10.2 banner). Routed under
  // v1/conversations/:id/voice_calls so it inherits the conversation's
  // access policy (not the admin-only voice gate). Returns lightweight
  // summaries — when the user expands a card the existing #show endpoint
  // serves the full transcript + segments.
  listForConversation(accountId, conversationId) {
    return axios.get(
      `/api/v1/accounts/${accountId}/conversations/${conversationId}/voice_calls`
    );
  }
}

// VoiceCosts — single endpoint that powers the §10.7 cost dashboard.
class VoiceCosts extends ApiClient {
  constructor() {
    super('voice', { apiVersion: 'v2', accountScoped: true });
  }

  summary(window = 'month_to_date', { from = null, to = null } = {}) {
    return axios.get(`${this.url}/cost_summary`, {
      params: { window, from, to },
    });
  }
}

// VoiceAudience — §10.5 wizard picker preview. Separate class from
// VoiceCosts because semantically the audience picker is part of the
// campaign-creation flow, not the cost dashboard.
class VoiceAudience extends ApiClient {
  constructor() {
    super('voice', { apiVersion: 'v2', accountScoped: true });
  }

  preview({ inboxId, labelIds = [], lastSeenWithinDays = null } = {}) {
    return axios.get(`${this.url}/audience_preview`, {
      params: {
        inbox_id: inboxId,
        label_ids: labelIds,
        last_seen_within_days: lastSeenWithinDays,
      },
    });
  }
}

// VoiceCampaigns — manager-facing list + detail for outbound campaigns
// (§10.5). Phase 1 is read-only; create/update arrive with the 7-step
// wizard UX.
class VoiceCampaigns extends ApiClient {
  constructor() {
    super('voice/campaigns', { apiVersion: 'v2', accountScoped: true });
  }

  list(params = {}) {
    return axios.get(this.url, { params });
  }

  show(campaignId) {
    return axios.get(`${this.url}/${campaignId}`);
  }

  create(payload) {
    return axios.post(this.url, { campaign: payload });
  }

  activate(campaignId) {
    return axios.post(`${this.url}/${campaignId}/activate`);
  }

  pause(campaignId) {
    return axios.post(`${this.url}/${campaignId}/pause`);
  }

  resume(campaignId) {
    return axios.post(`${this.url}/${campaignId}/resume`);
  }

  cancel(campaignId) {
    return axios.post(`${this.url}/${campaignId}/cancel`);
  }
}

// VoiceSearch — Sprint 4 UC-2 semantic search across past voice calls.
// Manager dashboard hits this on every query + filter submit.
class VoiceSearch extends ApiClient {
  constructor() {
    super('voice', { apiVersion: 'v2', accountScoped: true });
  }

  search({
    query,
    sentiment = null,
    inboxId = null,
    startedAtFrom = null,
    startedAtTo = null,
    direction = null,
    limit = 25,
  } = {}) {
    return axios.post(`${this.url}/search`, {
      query,
      sentiment,
      inbox_id: inboxId,
      started_at_from: startedAtFrom,
      started_at_to: startedAtTo,
      direction,
      limit,
    });
  }
}

// VoiceOverview — Sprint 4 polish landing page. One round-trip,
// returns KPIs + sentiment trend + recent calls + quick stats.
class VoiceOverview extends ApiClient {
  constructor() {
    super('voice', { apiVersion: 'v2', accountScoped: true });
  }

  show({ windowDays = 30, from = null, to = null } = {}) {
    return axios.get(`${this.url}/overview`, {
      params: { window_days: windowDays, from, to },
    });
  }
}

export default new VoiceInboxConfig();
export const voiceCallsAPI = new VoiceCalls();
export const voiceCampaignsAPI = new VoiceCampaigns();
export const voiceCostsAPI = new VoiceCosts();
export const voiceAudienceAPI = new VoiceAudience();
export const voiceSearchAPI = new VoiceSearch();
export const voiceOverviewAPI = new VoiceOverview();
